/**
 * Department Status — the default view.
 *
 * Five KPIs across the top, then one full-width card per department. Every number on this
 * page, including all five KPIs, comes from the same per-department signal that colours the
 * cards, so nothing on the strip can disagree with a card beneath it (UAT §1).
 */
import { useMemo } from 'react';
import { computeKpiStrip } from '@capacitea/engine';
import { useModel, useDepartmentSignals } from '../state.jsx';
import { DeptCard } from '../components/DeptCard.jsx';

export { statusOf } from '../components/DeptCard.jsx';

function Kpi({ label, value, sub, tone, accent }) {
  return (
    <div className={`kpi ${accent}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={tone ? { color: tone } : undefined}>{value}</div>
      <div className="kpi-sub">{sub}</div>
    </div>
  );
}

export function DepartmentStatus() {
  const { state, engine, activeDept, setActiveDept } = useModel();
  const signals = useDepartmentSignals();

  const scoped = useMemo(
    () => signals.filter(({ dep }) => activeDept === 'ALL' || dep.id === activeDept),
    [signals, activeDept],
  );

  // Computed by the shared engine, from the same signals that colour the cards below —
  // which is what makes "nothing on this strip can disagree with a card" structural.
  const kpis = useMemo(
    () => (state && engine ? computeKpiStrip(state, engine, activeDept) : null),
    [state, engine, activeDept],
  );

  if (!kpis) return null;

  const setDept = setActiveDept;

  return (
    <div className="page">
      <div className="page-hdr">
        <div className="page-hdr-title">Department Status</div>
        <div className="page-hdr-sub">Real-time capacity status · click any card to expand</div>
      </div>

      <div className="filter-bar">
        <label htmlFor="dept-filter">Department:</label>
        <select id="dept-filter" value={activeDept} onChange={(e) => setDept(e.target.value)}>
          <option value="ALL">All Departments</option>
          {state.depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>

      <div className="kpi-grid kpi-5">
        <Kpi
          label="Filled Headcount"
          value={kpis.filled}
          accent={kpis.vacant > 0 ? 'amber' : 'blue'}
          sub={
            kpis.vacant > 0
              ? <span style={{ color: 'var(--amber)' }}>⬜ {kpis.vacant} vacant</span>
              : `${kpis.deptCount} dept${kpis.deptCount !== 1 ? 's' : ''}`
          }
        />
        <Kpi
          label="Flagged FTE Gap"
          value={kpis.flaggedGap.toFixed(2)}
          tone={kpis.flaggedGap > 0 ? 'var(--red)' : 'var(--grn)'}
          accent={kpis.flaggedGap > 0 ? 'red' : 'green'}
          sub="Productive FTE short, org-wide"
        />
        <Kpi
          label="Coverage Gaps"
          value={kpis.coverageGaps}
          tone={kpis.coverageGaps > 0 ? 'var(--red)' : 'var(--grn)'}
          accent={kpis.coverageGaps > 0 ? 'red' : 'green'}
          sub="uncovered time blocks across trading &amp; desks"
        />
        <Kpi
          label="Action Points"
          value={kpis.actionPoints}
          accent={kpis.actionPoints > 0 ? 'red' : kpis.watchCount > 0 ? 'amber' : 'green'}
          sub={
            <>
              {kpis.watchCount} monitoring · {kpis.healthyCount} healthy ·{' '}
              {kpis.notConfiguredCount} not configured
              {kpis.dataInvalidCount > 0 && (
                <> · <strong style={{ color: 'var(--purple)' }}>{kpis.dataInvalidCount} data invalid</strong></>
              )}
            </>
          }
        />
        <Kpi
          label="Open Reqs"
          value={kpis.openReqs}
          accent={kpis.openReqs > 0 ? 'amber' : 'green'}
          sub={
            <>
              {kpis.openReqs > 0 ? 'active recruiting needed' : 'all eligible seats filled'}
              {kpis.blockedReqs > 0 && (
                <span style={{ color: 'var(--ink4)' }}> · {kpis.blockedReqs} more pending approval/frozen</span>
              )}
            </>
          }
        />
      </div>

      <div className="overview-grid">
        {scoped.map(({ dep, signal, severity }) => (
          <DeptCard key={dep.id} dep={dep} signal={signal} severity={severity} onFilter={setDept} />
        ))}
      </div>
    </div>
  );
}
